import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Stack,
  ActionIcon,
  Alert,
} from "@mantine/core";
import { IconArrowLeft, IconFileText } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useRFQ } from "../hooks/useRFQ";
import { poApi } from "../../../api/po";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";

export function RFQAward() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: rfq, isLoading } = useRFQ(id);
  const queryClient = useQueryClient();

  const generatePO = useMutation({
    mutationFn: (quotationId: string) =>
      poApi.generateDraft(rfq!.id, quotationId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["rfq", id] });
      notifications.show({
        title: "Success",
        message: "PO draft generated successfully",
        color: "green",
      });
      navigate(`/purchase-orders/${data.id}`);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to generate PO",
        color: "red",
      });
    },
  });

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading...</Text>
      </Container>
    );
  }

  const quotation = rfq?.awardedQuotationId 
    ? rfq.quotations?.find(q => q.id === rfq.awardedQuotationId)
    : undefined;

  if (!rfq || !quotation) {
    return (
      <Container size="xl" py="xl">
        <Group mb="xl">
          <ActionIcon variant="subtle" onClick={() => navigate(`/rfq/${id}`)}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>Award Details</Title>
        </Group>
        <Alert color="yellow">RFQ has not been awarded yet</Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate(`/rfq/${id}`)}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>RFQ Award Details</Title>
      </Group>

      <Stack gap="md">
        <Paper shadow="sm" p="md">
          <Title order={4} mb="md">
            Awarded Quotation
          </Title>
          <Stack gap="sm">
            <Group>
              <Text fw={500} w={200}>
                Quotation Number:
              </Text>
              <Text>{quotation.quotationNumber}</Text>
            </Group>
            <Group>
              <Text fw={500} w={200}>
                Vendor:
              </Text>
              <Text>
                {quotation.vendor.businessName || quotation.vendor.email}
              </Text>
            </Group>
            <Group>
              <Text fw={500} w={200}>
                Total Price:
              </Text>
              <Text fw={600} size="lg">
                {quotation.currency} {Number(quotation.totalPrice).toFixed(2)}
              </Text>
            </Group>
            <Group>
              <Text fw={500} w={200}>
                Delivery Days:
              </Text>
              <Text>{quotation.deliveryDays} days</Text>
            </Group>
            <Group>
              <Text fw={500} w={200}>
                Validity Days:
              </Text>
              <Text>{quotation.validityDays} days</Text>
            </Group>
            {quotation.paymentTerms && (
              <Group>
                <Text fw={500} w={200}>
                  Payment Terms:
                </Text>
                <Text>{quotation.paymentTerms}</Text>
              </Group>
            )}
            {quotation.notes && (
              <Group>
                <Text fw={500} w={200}>
                  Notes:
                </Text>
                <Text style={{ flex: 1 }}>{quotation.notes}</Text>
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md">
          <Title order={4} mb="md">
            Status Tracking
          </Title>
          <Stack gap="sm">
            <Group>
              <Badge color="green" variant="light" size="lg">
                RFQ Awarded
              </Badge>
              <Text size="sm" c="dimmed">
                {dayjs(rfq.updatedAt).format("DD MMM YYYY HH:mm")}
              </Text>
            </Group>
            {rfq.purchaseOrderId ? (
              <Group>
                <Badge color="blue" variant="light" size="lg">
                  PO Generated
                </Badge>
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => navigate(`/purchase-orders/${rfq.purchaseOrderId}`)}
                >
                  View PO
                </Button>
              </Group>
            ) : (
              <Group>
                <Badge color="gray" variant="light" size="lg">
                  PO Not Generated
                </Badge>
              </Group>
            )}
          </Stack>
        </Paper>

        {!rfq.purchaseOrderId && (
          <Group justify="flex-end">
            <Button
              leftSection={<IconFileText size={16} />}
              onClick={() => generatePO.mutate(quotation.id)}
              loading={generatePO.isPending}
            >
              Generate PO Draft
            </Button>
          </Group>
        )}
      </Stack>
    </Container>
  );
}

